import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  ConnectDropTarget,
  ConnectDragSource,
  DropTargetMonitor,
  DragSourceMonitor,
} from 'react-dnd';
import { DragSource, DropTarget, DropTargetConnector, DragSourceConnector } from 'react-dnd';
import classNames from 'classnames';
import { XYCoord } from 'dnd-core';

import './DragDropItem.scss';

interface IDragDropProps {
  id: string;
  index: number;
  moveItem: (dragId: string, hoverId: string, dragUp: boolean) => void;

  isDragging: boolean;
  className?: string;
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
}

interface IDragObject {
  id: string;
  index: number;
}

interface IDragDropInstance {
  getNode: () => HTMLDivElement | null;
}

const DragDropItem = forwardRef<HTMLDivElement, IDragDropProps>(function DragDropItem(
  { children, isDragging, connectDragSource, connectDropTarget, className },
  ref,
) {
  const elementRef = useRef(null);
  connectDragSource(elementRef);
  connectDropTarget(elementRef);
  useImperativeHandle<any, IDragDropInstance>(ref, () => ({
    getNode: () => elementRef.current,
  }));
  return (
    <div
      ref={elementRef}
      className={classNames('DragDropItem', className, { 'is-dragging': isDragging })}
    >
      {children}
    </div>
  );
});

const DropTargetType = 'DROP_TYPE';
export default DropTarget(
  DropTargetType,
  {
    hover(props: IDragDropProps, monitor: DropTargetMonitor, component: IDragDropInstance) {
      if (!component) {
        return null;
      }
      // node = HTML Div element from imperative API
      const node = component.getNode();
      if (!node) {
        return null;
      }

      const dragItem = monitor.getItem<IDragObject>();
      const dragIndex = dragItem.index;
      const hoverIndex = props.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = node.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      props.moveItem(dragItem.id, props.id, dragIndex > hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      monitor.getItem<IDragObject>().index = hoverIndex;
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  }),
)(
  DragSource(
    DropTargetType,
    {
      beginDrag: (props: IDragDropProps) => ({
        id: props.id,
        index: props.index,
      }),
    },
    (connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
      connectDragSource: connect.dragSource(),
      isDragging: monitor.isDragging(),
    }),
  )(DragDropItem),
);
